import { Box, MenuItem, TextField, Typography } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useState } from 'react'
import { type CollectionDistrict } from '../../../../../models/peerToPeer/Collection'
import { MultiSelectWithChips } from '../../../../customComponents/MultiSelectWithChips'
import { WizardFooter } from './WizardFooter'
import { stepStyles } from './stepStyles'

interface Props {
  districts: CollectionDistrict[]
  onContinue: (districtId: number, buildingIds: number[]) => void
}

export const GettingStartedStep = ({ districts, onContinue }: Props): JSX.Element => {
  const [districtId, setDistrictId] = useState<number | ''>('')
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<number[]>([])

  const district = districts.find(d => d.districtOrganizationId === districtId)
  const buildingItems = (district?.buildings ?? []).map(b => ({ id: b.organizationBuildingId, name: b.name }))

  return (
    <Box data-testid='p2pGettingStartedStep'>
      <Typography variant='h3'>Getting Started</Typography>
      <Typography variant='body2' sx={stepStyles.intro}>
        Start by selecting the district and then the corresponding buildings you&apos;ll be reporting for.
      </Typography>

      <Box sx={stepStyles.sections}>
        <Box sx={stepStyles.section}>
          <Typography variant='h4'>District</Typography>
          <Box sx={stepStyles.formField}>
            <Box sx={stepStyles.fieldText}>
              <Typography variant='body1'>Which District are you reporting for?</Typography>
              <Typography variant='body2'>This list includes your ISD operating as a District.</Typography>
            </Box>
            <TextField
              select
              fullWidth
              value={districtId}
              onChange={event => { setDistrictId(Number(event.target.value)); setSelectedBuildingIds([]) }}
              sx={stepStyles.select}
              data-testid='p2pDistrictSelect'
              inputProps={{ 'aria-label': 'District' }}
              SelectProps={{
                displayEmpty: true,
                IconComponent: KeyboardArrowDownIcon,
                renderValue: (selected: unknown): JSX.Element | string => selected === ''
                  ? <Box component='span' sx={stepStyles.selectPlaceholder}>Select District</Box>
                  : districts.find(d => d.districtOrganizationId === selected)?.name ?? '',
                MenuProps: {
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  PaperProps: { sx: stepStyles.selectMenu }
                }
              }}
            >
              {districts.map(d => (
                <MenuItem key={d.districtOrganizationId} value={d.districtOrganizationId}>{d.name}</MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        {district != null &&
          <Box sx={stepStyles.section} data-testid='p2pBuildingSelect'>
            <Typography variant='h4'>Building</Typography>
            <Box sx={stepStyles.formField}>
              <Box sx={stepStyles.fieldText}>
                <Typography variant='body1'>Which building(s) are you reporting for?</Typography>
                <Typography variant='body2'>
                  Select all buildings within this district you&apos;ll be completing this report for.
                </Typography>
              </Box>
              <Box>
                <MultiSelectWithChips
                  items={buildingItems}
                  selectedIds={selectedBuildingIds}
                  onChange={setSelectedBuildingIds}
                  placeholder='Select Building(s)'
                  ariaLabel='Buildings'
                  optionTestIdPrefix='p2pBuildingOption'
                  chipTestIdPrefix='p2pBuildingChip'
                />
              </Box>
            </Box>
          </Box>}
      </Box>

      <WizardFooter
        onContinue={() => { if (district != null) onContinue(district.districtOrganizationId, selectedBuildingIds) }}
        continueDisabled={district == null || selectedBuildingIds.length === 0}
        continueTestId='p2pGettingStartedContinue'
      />
    </Box>
  )
}
